// Per-objective "Ask the expert" link, shared by RecommendationCard and the
// Action Plan task list. Frames the objective (with its card for context and
// the element it moves at its current score) as a ready question and deep-links
// to the Head of Brand Marketing via useAskExpert — same prefill/dedupe/scope
// mechanics as The Fix's button. Kept as a quiet text link: these rows appear
// up to ~20 times on a grid, so a full button would shout.

import React from 'react';
import { useIntl } from 'react-intl';
import { formatBipolarScore } from '../config/bandColours';
import type { ScoreNode } from '../types';
import { THEME } from '../config/theme';
import { useAskExpert } from '../askExpert';
import type { Objective } from './ObjectiveChips';

export const ObjectiveAskExpert: React.FunctionComponent<{
  /** The recommendation/play title, for question context. */
  cardTitle?: string;
  objective: Objective;
  /** Resolve a `moves` element name → its ScoreNode (canonical or display). */
  resolve?: (name: string) => ScoreNode | undefined;
}> = ({ cardTitle, objective, resolve }) => {
  const intl = useIntl();
  const askExpert = useAskExpert();
  const task = objective.task;
  if (!task) return null;

  // AI-authored tasks/details rarely end in punctuation; joined into one
  // question they'd run together, so close each fragment as a sentence.
  const sentence = (txt: string): string =>
    /[.!?]$/.test(txt.trim()) ? txt.trim() : `${txt.trim()}.`;

  const handleClick = (): void => {
    const node = objective.moves ? resolve?.(objective.moves) : undefined;
    const score = node?.value != null ? formatBipolarScore(node.value) : undefined;
    const parts = [
      cardTitle &&
        intl.formatMessage(
          {
            id: 'marketerView.recos.promptCard',
            defaultMessage: 'My brand plan includes the recommendation "{title}".',
          },
          { title: cardTitle },
        ),
      intl.formatMessage(
        { id: 'marketerView.recos.promptObjective', defaultMessage: 'One of its objectives is: {task}' },
        { task: sentence(task) },
      ),
      objective.detail && sentence(objective.detail),
      objective.moves &&
        (score
          ? intl.formatMessage(
              {
                id: 'marketerView.recos.promptMovesScore',
                defaultMessage: 'It aims to improve {element}, currently at {score}.',
              },
              { element: node?.display ?? objective.moves, score },
            )
          : intl.formatMessage(
              { id: 'marketerView.recos.promptMoves', defaultMessage: 'It aims to improve {element}.' },
              { element: node?.display ?? objective.moves },
            )),
      intl.formatMessage({
        id: 'marketerView.recos.promptAsk',
        defaultMessage: 'How should I go about executing this for my brand?',
      }),
    ].filter(Boolean);
    askExpert(parts.join(' '));
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      style={{
        border: 'none',
        background: 'transparent',
        padding: 0,
        marginTop: 8,
        fontSize: 12.5,
        fontWeight: 600,
        color: THEME.primaryDark,
        cursor: 'pointer',
        display: 'block',
      }}
    >
      {intl.formatMessage({
        id: 'marketerView.recos.askExpert',
        defaultMessage: 'How can I do this? Ask the expert →',
      })}
    </button>
  );
};
